// @ts-check
import { freebetStore } from './freebet.store';
import { getTotalMatchItems } from './items';
import FreebetRequestService from '@bto-services/api/shared/freebet.request';
import { state } from '../state';
import { calculateBonuses } from './calculations';
import { saveState } from './helpers';
import { formatCurrency, daysDiff, hoursDiff, DateFilterService } from '@bto-services/utils';
import { showBetslipValidation } from '@bto-services/modal';
import { BETSLIP_VALIDATION_ERRORS, ITEM_TYPES } from 'bto-models/betslip';
import { configStore } from './config.store';


/**
 * @typedef {import('bto-models/betslip/interfaces').FreebetItem} FreebetItem
 */

/**
 * Load freebets into store
 * @param {{FreeBets:{[freebetId:number]:FreebetItem},ResponseMessage: string, ResponseCode: number}} data - data from api
 */
export function loadFreebets(data) {
  const freebets = Object.values(data.FreeBets);
  freebetStore.freebets = freebets;
}

/**
 * Get freebets for logged user
 */
export function getFreebets() {
  const freebetRequestService = new FreebetRequestService();
  freebetRequestService.execute((resp) => {
    if (resp.data) {
      loadFreebets(resp.data);
      // If freebet was selected and it's no longer available remove the freebet from the store
      if (state.freebetId && !resp.data.FreeBets[state.freebetId]) {
        removeFreebet();
      }
    }
  });
}

/**
 * Check if selected freebet is still available, if not remove it from store
 */
export function checkFreebet() {
  if (state.freebetId && !freebetStore.freebets.some(f => f.UserFreeBetID === state.freebetId)) {
    removeFreebet();
  }
}

/**
 * Set freebet in betslip
 * @param {FreebetItem} freebet - set freebet in betslip
 */
export function setFreebet(freebet) {
  if (configStore.settings.NO_BOOSTED_IN_FREEBETS && Object.values(state.items).some(i => i.itemType === ITEM_TYPES.BOOSTED_ODD)) {
    showBetslipValidation(BETSLIP_VALIDATION_ERRORS.BOOSTED_IN_FREEBET);
    return;
  }
  const combiFold = getTotalMatchItems();
  const folds = { [combiFold]: `${freebet.Stake}` };
  state.folds = folds;
  state.freebetId = freebet.UserFreeBetID;
  state.netFreebet = freebet.IsNetWinning;
  calculateBonuses();
  saveState();
}

/**
 * Remove freebet from betslip
 */
export function removeFreebet() {
  state.freebetId = 0;
  state.folds = {};
  delete state.netFreebet;
  calculateBonuses();
  saveState();
}

/**
 * Get freebet expiry warn time
 * @param {FreebetItem} freebet - freebet item
 */
export function getFreebetWarnTime(freebet) {
  const freebetWarnTime = APP_SETTINGS.betslip_v2.freebetWarnTime;
  if (freebetWarnTime) {
    let now = Date.now();
    const freebetDate = DateFilterService.convertToDateTime(freebet.ExpiryDate, true);
    const checkDays = freebetWarnTime.match(/\d+d/);
    if (checkDays) {
      let dv = 0;
      // get days differnce between saved and now minus the months from above if any
      let dDiff = daysDiff(now, freebetDate);
      let hDiff = hoursDiff(now, freebetDate);
      dv = parseFloat(checkDays[0]);
      if (hDiff < 24) {
        return { type: 'hours', data: hDiff };
      }
      if (dDiff < dv) {
        return { type: 'days', data: dDiff };
      }
    }
  }
  return;
}

/**
 * Add dynamic properties to string resource
 * @param {FreebetItem} freebet - freebet item
 * @param {string} text - translated string resource
 */
export function getUsedFreebetText(freebet, text = '') {
  const usedFreebets = freebet.NumberFreeBets - freebet.PlayedFreeBets;
  return text
    .replace('{numUsedFreebets}', `${usedFreebets}`)
    .replace('{numTotalFreebets}', `${freebet.NumberFreeBets}`)
    .replace('{stake}', formatCurrency(`${freebet.Stake}`));
}